/**
 * Boucle « une image de la vidéo → un appel », qui NE MEURT JAMAIS :
 * - `requestVideoFrameCallback` (rVFC) quand il existe, sinon `requestAnimationFrame` (rAF) ;
 * - si rVFC se tait alors que la vidéo joue (vidéo cachée mise en veille par le navigateur),
 *   bascule sur rAF ;
 * - si la vidéo cachée passe en pause (navigateur économe), elle est relancée ;
 * - une exception dans `onFrame` est comptée et signalée, jamais fatale.
 * Horloges injectables : testé sans navigateur.
 */
export type FrameLoopMode = 'rvfc' | 'raf';

/** Ce que la boucle lit sur l'élément vidéo (structurellement : `HTMLVideoElement`). */
export interface FrameLoopVideo {
  readonly readyState: number;
  readonly paused: boolean;
  readonly currentTime: number;
  requestVideoFrameCallback?(callback: () => void): unknown;
  play?(): Promise<void>;
}

export interface FrameLoopState {
  readonly mode: FrameLoopMode;
  /** Images transmises à `onFrame`. */
  readonly frames: number;
  /** Appels de `onFrame` qui ont levé une exception (la boucle a continué). */
  readonly errors: number;
  /** rVFC s'est tu alors que la vidéo jouait : passé sur rAF. */
  readonly stalled: boolean;
  /** Vidéo trouvée en pause et relancée. */
  readonly resumes: number;
}

export interface FrameLoopOptions {
  readonly now?: () => number;
  readonly requestAnimationFrame?: (callback: () => void) => void;
  /** Sans image par rVFC pendant ce délai, vidéo en lecture : bascule sur rAF. */
  readonly stallMs?: number;
  /** rAF : si `currentTime` ne bouge pas, l'image est quand même relue à cet intervalle. */
  readonly staleMs?: number;
  readonly onError?: (error: unknown) => void;
}

export interface FrameLoop {
  stop(): void;
  state(): FrameLoopState;
}

const HAVE_CURRENT_DATA = 2;

export function startFrameLoop(
  video: FrameLoopVideo,
  onFrame: (t: number) => void,
  {
    now = () => performance.now(),
    requestAnimationFrame = (cb) => globalThis.requestAnimationFrame(cb),
    stallMs = 1000,
    staleMs = 100,
    onError,
  }: FrameLoopOptions = {},
): FrameLoop {
  let stopped = false;
  let mode: FrameLoopMode = video.requestVideoFrameCallback ? 'rvfc' : 'raf';
  let frames = 0;
  let errors = 0;
  let stalled = false;
  let resumes = 0;
  let lastTime = -1;
  let lastFrameAt = now();

  const hasData = (): boolean => video.readyState >= HAVE_CURRENT_DATA;
  const playing = (): boolean => hasData() && !video.paused;

  const deliver = (t: number): void => {
    lastFrameAt = t;
    lastTime = video.currentTime;
    frames++;
    try {
      onFrame(t);
    } catch (e) {
      errors++;
      onError?.(e);
    }
  };

  const rafTick = (): void => {
    if (stopped) return;
    const t = now();
    if (playing() && (video.currentTime !== lastTime || t - lastFrameAt >= staleMs)) deliver(t);
    requestAnimationFrame(rafTick);
  };

  const rvfcTick = (): void => {
    if (stopped || mode !== 'rvfc') return;
    if (playing()) deliver(now());
    video.requestVideoFrameCallback?.(rvfcTick);
  };

  const switchToRaf = (): void => {
    mode = 'raf';
    stalled = true;
    requestAnimationFrame(rafTick);
  };

  /** Vidéo en pause alors qu'elle a des données : relancée (le navigateur l'a mise en veille). */
  const resumeIfPaused = (): void => {
    if (!hasData() || !video.paused || !video.play) return;
    resumes++;
    video.play().catch(() => undefined);
  };

  const watchdog = setInterval(() => {
    if (stopped) return;
    resumeIfPaused();
    const t = now();
    if (!playing()) lastFrameAt = t;
    else if (mode === 'rvfc' && t - lastFrameAt >= stallMs) switchToRaf();
  }, stallMs / 2);

  if (mode === 'rvfc') video.requestVideoFrameCallback?.(rvfcTick);
  else requestAnimationFrame(rafTick);

  return {
    stop() {
      stopped = true;
      clearInterval(watchdog);
    },
    state: () => ({ mode, frames, errors, stalled, resumes }),
  };
}
